import { IListResource, IFieldValuesResource, IDetailResource, IActionResource } from '../../ts/resources/Resource';
import { IViewInfo, IViewDetailInfo, IViewBase } from '../../ts/models/IViewInfo';
import { IViewActions } from '../../ts/ServerApi/types/IViewActions';
import { ITaggableContent } from '../../ts/models/ITaggableContent';
import { ServerService } from '../../ts/services/ServerService';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { GetViewsRequest } from '../../ts/ServerApi/requests/GetViewsRequest';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { ViewMapper } from '../../ts/mappers/ViewMapper';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { ViewDefinition } from '../../ts/models/ContentDefinitions';
import { GetViewByPathRequest, IGetViewByPathResult } from '../../ts/ServerApi/requests/GetViewByPathRequest';
import { IWorkbookBase, IWorkbookParentInfo } from '../../ts/models/IWorkbookInfo';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { GetViewActionsRequest } from '../../ts/ServerApi/requests/GetViewActionsRequest';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { RenameViewRequest } from '../../ts/ServerApi/requests/RenameViewRequest';
import { RequestHelper } from '../../ts/helpers/RequestHelper';
import { HideViewsRequest } from '../../ts/ServerApi/requests/HideViewsRequest';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { AddTagsToViewsRequest } from '../../ts/ServerApi/requests/AddTagsToViewsRequest';
import { RemoveTagsFromViewsRequest } from '../../ts/ServerApi/requests/RemoveTagsFromViewsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { ServiceLocator } from '../../app/ServiceLocator';
import { ErrorNotifyingServer } from '../../ts/services/ErrorNotifyingServer';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { IPromise } from 'angular';

class ViewsResource implements IListResource<IViewInfo>, IFieldValuesResource, IDetailResource<IViewDetailInfo>, IActionResource<IViewActions>, ITaggableContent {

  private listFetcher: IListFetcher<IViewInfo> = new WebClientListResourceFetcher(GetViewsRequest, ServerApiMapper.views);
  private detailFetcher: IDetailFetcher<IViewDetailInfo> = new WebClientDetailFetcher('getView', ViewMapper.mapDetail)
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public isViewDetailInfo(item: any): item is IViewDetailInfo {
    return item.contentType &&
      item.contentType === ViewDefinition.contentType &&
      (<IViewDetailInfo>item).hasOwnProperty('sheetType');
  }

  public isViewBase(item: any): item is IViewBase {
    return item.contentType &&
      item.contentType === ViewDefinition.contentType &&
      item.hasOwnProperty('workbook');
  }

  public all(): ResourceQuery<IViewInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): IPromise<IViewDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getByPath(path: string): IPromise<IViewDetailInfo> {
    return ServiceLocator.get<ServerService>('ServerService').sendRequest(new GetViewByPathRequest(path))
      .then((result: IGetViewByPathResult) => {
        const error = result.errors && result.errors[0] && result.errors[0].code;
        if (error) {
          return Promise.reject(error);
        } else {
          return Promise.resolve(ViewMapper.mapDetail(result));
        }
      });
  }

  public forWorkbook(workbook: IWorkbookBase | IWorkbookParentInfo): ResourceQuery<IViewInfo> {
    return this.all().withFilters(ServerFilterClauses.create({ workbook: workbook.id }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'view');
  }

  // Actions

  public actions(id: string): IPromise<IViewActions> {
    return ServiceLocator.get<ServerService>('ServerService').sendRequest(new GetViewActionsRequest(id));
  }

  public rename(id: string, name: string): IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer').sendRequestAndDelay(new RenameViewRequest(id, name));
  }

  public hide(ids: string[]): IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer').sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new HideViewsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer').sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({}) as unknown as IPromise<IResult>;
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer').sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({}) as unknown as IPromise<IResult>;
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public markRecentlyViewed(id: string) {
    return ServiceLocator.get<ServerService>('ServerService').sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.View
    }));
  }
}

export const Views = new ViewsResource();
